import React from 'react';
import { Menu, MenuTrigger, MenuPopover, MenuList, MenuItemCheckbox, Button, MenuCheckedValueChangeData, MenuCheckedValueChangeEvent } from '@fluentui/react-components';
import { ColumnEditRegular } from '@fluentui/react-icons';
import { ColumnExtended } from './ColumnExtended';

export const STORAGE_KEY = 'dashboard-visible-columns';

interface ColumnPickerProps {
    allColumns: ColumnExtended[];
    visibleKeys: Set<string>;
    onVisibleKeysChange: (keys: Set<string>) => void;
}

/**
 * Reads the persisted set of visible optional column keys from localStorage.
 * Falls back to showing columns where defaultHidden is not true.
 */
export function loadVisibleColumns(allColumns: ColumnExtended[]): Set<string> {
    let stored: string | null = null;
    try {
        stored = localStorage.getItem(STORAGE_KEY);
    } catch {
        stored = null;
    }

    if (stored) {
        try {
            const parsed: unknown = JSON.parse(stored);
            if (Array.isArray(parsed)) {
                const validKeys = new Set(allColumns.filter(c => c.isOptional).map(c => c.key));
                const storedKeys = parsed.filter((k): k is string => typeof k === 'string');
                return new Set(storedKeys.filter(k => validKeys.has(k)));
            }
        } catch {
            // corrupt — fall through to defaults
        }
    }

    // Default: show optional columns that are NOT defaultHidden
    return new Set(
        allColumns.filter(c => c.isOptional && !c.defaultHidden).map(c => c.key)
    );
}

/** Persists the visible optional column keys to localStorage. */
function saveVisibleColumns(keys: Set<string>): void {
    try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(Array.from(keys)));
    } catch {
        // ignore storage failures (blocked/quota/etc.)
    }
}

/** Filters the full column list to only non-optional + visible optional columns. */
export function getVisibleColumns(allColumns: ColumnExtended[], visibleKeys: Set<string>): ColumnExtended[] {
    return allColumns.filter(c => !c.isOptional || visibleKeys.has(c.key));
}

export const ColumnPicker: React.FC<ColumnPickerProps> = ({ allColumns, visibleKeys, onVisibleKeysChange }) => {
    const optionalColumns = allColumns.filter(c => c.isOptional);

    const checkedValues: Record<string, string[]> = {
        columns: optionalColumns.filter(c => visibleKeys.has(c.key)).map(c => c.key),
    };

    const handleChange = (_e: MenuCheckedValueChangeEvent, data: MenuCheckedValueChangeData) => {
        const newKeys = new Set<string>(data.checkedItems);
        saveVisibleColumns(newKeys);
        onVisibleKeysChange(newKeys);
    };

    return (
        <Menu checkedValues={checkedValues} onCheckedValueChange={handleChange}>
            <MenuTrigger disableButtonEnhancement>
                <Button
                    icon={<ColumnEditRegular />}
                    title="Columns"
                    aria-label="Columns"
                    aria-description="Show or hide optional dashboard columns"
                    appearance="subtle"
                >
                    Columns
                </Button>
            </MenuTrigger>
            <MenuPopover>
                <MenuList>
                    {optionalColumns.map(col => (
                        <MenuItemCheckbox key={col.key} name="columns" value={col.key}>
                            {col.name}
                        </MenuItemCheckbox>
                    ))}
                </MenuList>
            </MenuPopover>
        </Menu>
    );
};
